/**
 * Pure helpers for {@link TicketSegmentTravelProgress}: map itinerary legs to their
 * purchasable segment position so the purchase progress bar can highlight the
 * segment currently being configured.
 */

import { type ItineraryDTO, type ItineraryLegDTO } from "../../types/dtos/api/itinerary/itinerary.dto";

/** One visible leg of the purchase progress bar (itinerary leg plus its position). */
export interface TicketSegmentProgressVisibleLeg {

    /** Itinerary leg to render. */
    "leg": ItineraryLegDTO;

    /** Position of the leg inside `itinerary.legs`. */
    "legIndex": number;
}

/**
 * One rendered unit of the purchase progress bar: either a `segment` group holding
 * every consecutive visible leg of one purchasable segment, or an `untracked`
 * unit holding a single leg outside all purchasable segments.
 */
export interface TicketSegmentProgressUnit {

    /** Whether the unit groups a purchasable segment or a lone untracked leg. */
    "kind": "segment" | "untracked";

    /** Position inside `purchasableSegments` when `kind === "segment"`, otherwise `null`. */
    "segmentPosition": number | null;

    /** Visible legs of the unit in itinerary order (exactly one for `untracked`). */
    "legs": TicketSegmentProgressVisibleLeg[];
}

/**
 * Groups consecutive visible legs by purchasable segment position so each segment
 * renders as one visual block. Legs not covered by any purchasable segment stay
 * standalone `untracked` units.
 *
 * @param visibleLegs - Legs to render, in itinerary order.
 * @param positionByLegIndex - Map from leg index to purchasable segment position.
 * @returns Ordered render units for the progress bar.
 */
export const buildTicketSegmentProgressUnits = (
    visibleLegs: readonly TicketSegmentProgressVisibleLeg[],
    positionByLegIndex: ReadonlyMap<number, number>,
): TicketSegmentProgressUnit[] => {
    const units: TicketSegmentProgressUnit[] = [];

    for ( const entry of visibleLegs ) {
        const position = positionByLegIndex.get( entry.legIndex );

        if ( position === undefined ) {
            units.push( {
                "kind":            "untracked",
                "segmentPosition": null,
                "legs":            [ entry ],
            } );

            continue;
        }

        const lastUnit = units[units.length - 1];

        if ( lastUnit !== undefined && lastUnit.kind === "segment" && lastUnit.segmentPosition === position ) {
            lastUnit.legs.push( entry );

            continue;
        }

        units.push( {
            "kind":            "segment",
            "segmentPosition": position,
            "legs":            [ entry ],
        } );
    }

    return units;
};

/**
 * Resolves, for each itinerary leg index, the 0-based position of the owning
 * purchasable segment inside `purchasableSegmentIds`.
 *
 * @remarks Resolution order per segment, by decreasing reliability:
 * 1. `pricingSegments[].legRange` (`firstLegIndex..lastLegIndex`) — validated
 *    against the legs array like `buildSegmentRangeVisualStates` in the
 *    itinerary details panel;
 * 2. `pricingSegments[].legIds` joined through `itinerary.legIndexForId`;
 * 3. per-leg `leg.priceEvaluationSegmentId` as a last-resort fallback.
 *
 * Legs whose owning segment is not purchasable (or that cannot be resolved at
 * all) are simply absent from the returned map.
 *
 * @param itinerary - Itinerary carrying `legs`, `pricingSegments`, and `legIndexForId`.
 * @param purchasableSegmentIds - Ordered purchasable segment ids (purchase step order).
 * @returns Map of leg index to purchasable segment position.
 */
export const resolvePurchasableSegmentIndexByLegIndex = (
    itinerary: ItineraryDTO,
    purchasableSegmentIds: readonly string[],
): Map<number, number> => {
    const positionByLegIndex = new Map<number, number>();

    if ( purchasableSegmentIds.length === 0 || itinerary.legs.length === 0 ) {
        return positionByLegIndex;
    }

    const positionBySegmentId = new Map( purchasableSegmentIds.map( ( id, index ) => [ id, index ] ) );
    const maxLegIndex = itinerary.legs.length - 1;

    for ( const segment of itinerary.pricingSegments ?? [] ) {
        const position = positionBySegmentId.get( segment.id );

        if ( position === undefined ) {
            continue;
        }

        const firstLegIndex = segment.legRange?.firstLegIndex;
        const lastLegIndex = segment.legRange?.lastLegIndex;

        if (
            firstLegIndex != null &&
            lastLegIndex != null &&
            firstLegIndex >= 0 &&
            lastLegIndex >= firstLegIndex &&
            lastLegIndex <= maxLegIndex
        ) {
            for ( let legIndex = firstLegIndex; legIndex <= lastLegIndex; legIndex++ ) {
                positionByLegIndex.set( legIndex, position );
            }

            continue;
        }

        for ( const legId of segment.legIds ?? [] ) {
            const legIndex = itinerary.legIndexForId?.[legId];

            if ( legIndex !== undefined ) {
                positionByLegIndex.set( legIndex, position );
            }
        }
    }

    itinerary.legs.forEach( ( leg, legIndex ) => {
        if ( positionByLegIndex.has( legIndex ) ) {
            return;
        }

        const ownerSegmentId = leg.priceEvaluationSegmentId;

        if ( ownerSegmentId == null ) {
            return;
        }

        const position = positionBySegmentId.get( ownerSegmentId );

        if ( position !== undefined ) {
            positionByLegIndex.set( legIndex, position );
        }
    } );

    return positionByLegIndex;
};
